import { storeToRefs } from 'pinia'
import { nextTick } from 'vue'
import { useEditorStore } from '@/stores/editor.ts'
import { useCommonStore } from '@/stores/common.ts'
import { useCreateTable } from '@/views/editor/composable/table/useCreateTable.ts'
import { setDataType, socketSave, deletePrefix } from '@/views/editor/composable/useNote.ts'
import { UseHistory } from '@/views/editor/composable/useHistory.ts'
import { calcFunction, clampPositionString } from '@/views/editor/composable/table/useFunction.ts'

function toColumnLabel(index: number): string {
	let label = ''
	let num = index
	while (num > 0) {
		const remainder = (num - 1) % 26
		label = String.fromCharCode(65 + remainder) + label
		num = Math.floor((num - 1) / 26)
	}
	return label
}

export function useMergeTable() {
	async function merge() {
		const { dragList, tableSelector } = storeToRefs(useEditorStore())

		if (dragList.value.length < 2) return

		// 1. 선택된 셀들의 좌표 범위 계산
		const positions: any[] = []
		const processedCells = new Set()
		const processedPositions = new Set()

		dragList.value.forEach((item: any) => {
			if (processedCells.has(item)) return
			processedCells.add(item)

			tableSelector.value.forEach((row: any, rowIndex: number) => {
				row.forEach((cell: any, colIndex: number) => {
					if (cell.html === item) {
						const posKey = `${rowIndex},${colIndex}`
						if (!processedPositions.has(posKey)) {
							processedPositions.add(posKey)
							positions.push({ row: rowIndex, col: colIndex })
						}
					}
				})
			})
		})

		if (positions.length === 0) return

		let minRow = Math.min(...positions.map(p => p.row))
		let maxRow = Math.max(...positions.map(p => p.row))
		let minCol = Math.min(...positions.map(p => p.col))
		let maxCol = Math.max(...positions.map(p => p.col))

		// 2. 병합 셀을 고려한 확장 범위 계산
		function findMergedCellOrigin(cellDom: any): { row: number; col: number } | null {
			for (let r = 0; r < tableSelector.value.length; r++) {
				for (let c = 0; c < tableSelector.value[r].length; c++) {
					if (tableSelector.value[r][c]?.html === cellDom) {
						return { row: r, col: c }
					}
				}
			}
			return null
		}

		const rePositions: any[] = []
		const processedMergedCells = new Set()

		for (let i = minRow; i <= maxRow; i++) {
			for (let j = minCol; j <= maxCol; j++) {
				const cell = tableSelector.value[i]?.[j]
				if (!cell?.html) continue

				if (processedMergedCells.has(cell.html)) continue
				processedMergedCells.add(cell.html)

				const rowSpan = cell.html.rowSpan || 1
				const colSpan = cell.html.colSpan || 1

				const origin = findMergedCellOrigin(cell.html)
				if (!origin) continue

				for (let r = 0; r < rowSpan; r++) {
					for (let c = 0; c < colSpan; c++) {
						rePositions.push({
							row: origin.row + r,
							col: origin.col + c,
						})
					}
				}
			}
		}

		// 3. 확장된 최종 범위 재계산
		if (rePositions.length > 0) {
			minRow = Math.min(...rePositions.map((pos: any) => pos.row))
			maxRow = Math.max(...rePositions.map((pos: any) => pos.row))
			minCol = Math.min(...rePositions.map((pos: any) => pos.col))
			maxCol = Math.max(...rePositions.map((pos: any) => pos.col))
		}

		// 4. 병합될 셀들 중 첫 번째 셀(기준 셀) 식별
		// tableSelector[minRow][minCol]이 기준 셀이 됨
		const targetCellWrapper = tableSelector.value[minRow][minCol]
		const targetCell = targetCellWrapper.html
		const totalRowSpan = maxRow - minRow + 1
		const totalColSpan = maxCol - minCol + 1

		// 5. 셀 제거 전 각 행의 실제 높이를 데이터 셀 div에서 수집
		const savedHeights: number[] = []
		const processedRowHeights = new Set<number>()
		for (let r = minRow; r <= maxRow; r++) {
			if (processedRowHeights.has(r)) continue
			processedRowHeights.add(r)
			let rowHeight = 40
			for (let c = minCol; c <= maxCol; c++) {
				const cell = tableSelector.value[r]?.[c]
				if (!cell?.html || cell.html.classList.contains('number--line')) continue
				if (cell.html.rowSpan > 1) continue
				const innerDiv = cell.html.querySelector('div') as HTMLElement
				if (innerDiv?.style.height) {
					rowHeight = Math.round(parseFloat(innerDiv.style.height))
				}
				break
			}
			savedHeights.push(rowHeight)
		}

		// 6. 기준 셀 업데이트
		targetCell.rowSpan = totalRowSpan
		targetCell.colSpan = totalColSpan

		// 7. 병합되는 나머지 셀들 제거 (dragList 기반)
		const removedIds = new Set<string>()
		const keptId = targetCell.id

		tableSelector.value.forEach((row: any, rowIndex: number) => {
			row.forEach((cell: any, cellIndex: number) => {
				if (rowIndex >= minRow && rowIndex <= maxRow && cellIndex >= minCol && cellIndex <= maxCol) {
					if (cell.html.id === keptId) return // targetCell skip
					if (removedIds.has(cell.html.id)) return // 중복 skip

					cell.html.remove()
					removedIds.add(cell.html.id)
				}
			})
		})

		// 7.5. tableSelector 업데이트 (제거 후)
		const table = targetCell.closest('table') as HTMLTableElement
		if (table) {
			useCreateTable().tableShapes(table)
		}

		// 7.6. 병합으로 사라진 셀의 수식 참조 제거
		const removedPositions = new Set<string>()
		for (let r = minRow; r <= maxRow; r++) {
			for (let c = minCol; c <= maxCol; c++) {
				if (r === minRow && c === minCol) continue
				removedPositions.add(toColumnLabel(c + 1) + String(r + 1))
			}
		}

		if (removedPositions.size > 0) {
			tableSelector.value.forEach((row: any) => {
				row.forEach((cell: any) => {
					if (cell.html?.dataset?.type === 'function' && cell.html?.dataset?.position) {
						const updated = cell.html.dataset.position
							.split(',')
							.map((item: string) => item.trim())
							.filter((item: string) => {
								if (item.includes(':')) {
									const [start, end] = item.split(':').map((s: string) => s.trim())
									return !removedPositions.has(start) && !removedPositions.has(end)
								}
								return !removedPositions.has(item)
							})
							.join(',')
						cell.html.dataset.position = updated
					}
				})
			})
		}

		// 8. 변경 사항 저장 및 처리
		await finalizeTableChange(targetCell, savedHeights)
	}

	async function unmerge() {
		const { dragList, cellTarget } = storeToRefs(useEditorStore())

		let target = null
		if (cellTarget.value) {
			target = cellTarget.value
		} else if (dragList.value.length > 0) {
			target = dragList.value[0]
		}

		if (target) {
			await unmergeTarget(target as HTMLTableCellElement)
		}
	}

	async function unmergeTarget(targetCell: HTMLTableCellElement) {
		const { tableSelector } = storeToRefs(useEditorStore())
		const table = targetCell.closest('table') as HTMLTableElement

		if (!targetCell || !table) return

		const rowSpan = targetCell.rowSpan
		const colSpan = targetCell.colSpan

		if (rowSpan === 1 && colSpan === 1) return

		// 현재 셀의 tableSelector 상의 위치 찾기 (성능 최적화: 찾으면 즉시 중단)
		let startRow = -1
		let startCol = -1

		for (let rowIndex = 0; rowIndex < tableSelector.value.length; rowIndex++) {
			for (let colIndex = 0; colIndex < tableSelector.value[rowIndex].length; colIndex++) {
				if (tableSelector.value[rowIndex][colIndex].html === targetCell) {
					startRow = rowIndex
					startCol = colIndex
					break
				}
			}
			if (startRow !== -1) break
		}

		if (startRow === -1 || startCol === -1) return

		// 각 행의 높이 가져오기 (data-size에서)
		let heights: number[] = []
		const firstDataRow = table.rows[1]
		if (firstDataRow) {
			const firstDataCell = firstDataRow.querySelector('td:not(.number--line)') as HTMLElement
			if (firstDataCell && firstDataCell.dataset.size) {
				try {
					const sizeData = JSON.parse(firstDataCell.dataset.size)
					heights = sizeData.height || []
				} catch (e) {
					// ignore
				}
			}
		}

		// 1. 기준 셀 속성 초기화 먼저 (DOM 삽입 전에 병합 해제)
		targetCell.rowSpan = 1
		targetCell.colSpan = 1
		const targetDiv = targetCell.querySelector('div') as HTMLElement
		if (targetDiv) {
			const targetRowHeight = heights[startRow] || 40
			targetDiv.style.height = `${targetRowHeight}px`
		}

		// 2. DOM에 새 셀 삽입
		for (let r = startRow; r < startRow + rowSpan; r++) {
			// 올바른 tr 찾기: DOM 직접 접근 (table.rows[r + 1]은 헤더 행 제외)
			const tr = table.rows[r + 1]

			if (!tr) continue

			const rowHeight = heights[r] || 40

			for (let c = startCol; c < startCol + colSpan; c++) {
				// 기준 셀 위치(startRow, startCol)는 이미 존재하므로 건너뜀
				if (r === startRow && c === startCol) continue

				const newCell = document.createElement('td')
				newCell.id = useCommonStore().uniqueId()
				newCell.colSpan = 1
				newCell.rowSpan = 1

				const newDiv = document.createElement('div')
				newDiv.setAttribute('contenteditable', 'true')
				newDiv.style.height = `${rowHeight}px`
				newCell.appendChild(newDiv)

				// 삽입 위치 찾기: 실제 DOM의 tr 자식 순서 기준
				let insertBeforeCell: HTMLTableCellElement | null = null

				for (let i = 0; i < tr.children.length; i++) {
					const cell = tr.children[i] as HTMLTableCellElement
					if (cell.classList.contains('number--line')) continue

					// tableSelector에서 이 셀의 열 인덱스 찾기
					let cellColIndex = -1
					for (let ci = 0; ci < tableSelector.value[r].length; ci++) {
						if (tableSelector.value[r][ci].html === cell) {
							cellColIndex = ci
							break
						}
					}

					if (cellColIndex >= c + 1) {
						insertBeforeCell = cell
						break
					}
				}

				// 삽입
				if (insertBeforeCell) {
					tr.insertBefore(newCell, insertBeforeCell)
				} else {
					tr.appendChild(newCell)
				}
			}
		}

		// 3. tableSelector 업데이트 (새로 추가된 셀들 반영)
		await nextTick()
		if (table) {
			useCreateTable().tableShapes(table)
		}

		// 4. 변경 사항 저장
		await finalizeTableChange(targetCell)
	}

	async function finalizeTableChange(targetCell: HTMLTableCellElement, savedHeights?: number[]) {
		const { selector, blockList, tableSelector, isUndoRedoInProgress, cellTarget } = storeToRefs(useEditorStore())
		const table = targetCell.closest('table') as HTMLTableElement

		if (!table) return

		// Undo/Redo 중에는 히스토리 저장하지 않음
		const wasUndoRedo = isUndoRedoInProgress.value

		// 병합/해제 중에는 자동 히스토리 저장 방지
		if (!wasUndoRedo) {
			isUndoRedoInProgress.value = true
		}

		// DOM 사이즈 계산 (저장용)
		const allSize: any = {
			width: [],
			height: [],
		}

		const rowList = [...table.rows]
		const firstRowCells = rowList[0].querySelectorAll('td.number--line')

		for (let i = 1; i < firstRowCells.length; i++) {
			const cell = firstRowCells[i] as HTMLElement
			const cellWidth = cell.style.width ? Math.round(parseFloat(cell.style.width)) : 120
			allSize.width.push(cellWidth)
		}

		const dataRowCount = rowList.length - 1
		if (savedHeights && savedHeights.length === dataRowCount) {
			allSize.height = [...savedHeights]
		} else {
			rowList.forEach((row: any, rowIndex: number) => {
				if (rowIndex !== 0) {
					const cellList = [...row.cells]
					let rowHeight = 40
					for (const cell of cellList) {
						if (cell.classList.contains('number--line')) continue
						if (cell.rowSpan > 1) continue
						const innerDiv = cell.querySelector('div') as HTMLElement
						if (innerDiv?.style.height) {
							rowHeight = Math.round(parseFloat(innerDiv.style.height))
						}
						break
					}
					allSize.height.push(rowHeight)
				}
			})
		}

		// tableSelector 업데이트는 innerHTML 재생성 후 수행됨 (Line 417)

		// 데이터 모델(content.cell) 재구축
		const rows: any[] = []
		const overCell = new Set<string>()

		tableSelector.value.forEach((rowList: any, rowIndex: number) => {
			const newRow: any[] = []
			rowList.forEach((cell: any, colIndex: number) => {
				if (!cell?.html) {
					// 빈 셀도 데이터에 포함
					newRow.push({
						cellUid: useCommonStore().uniqueId(),
						colSpan: 1,
						rowSpan: 1,
						content: '',
						dataType: {
							testid: `editor-block-inner-table-cell-${rowIndex}${colIndex}`,
							colSpan: '1',
							rowSpan: '1',
						},
					})
					return
				}

				// 셀의 원점인지 확인 (병합된 셀의 경우 왼쪽 상단만 원점)
				let isOrigin = true

				// rowSpan의 일부인지 확인 (이전 행에 같은 cell이 있으면 원점 아님)
				if (rowIndex > 0 && tableSelector.value[rowIndex - 1][colIndex]?.html === cell.html) {
					isOrigin = false
				}

				// colSpan의 일부인지 확인 (이전 열에 같은 cell이 있으면 원점 아님)
				if (colIndex > 0 && rowList[colIndex - 1]?.html === cell.html) {
					isOrigin = false
				}

				if (isOrigin && !overCell.has(cell.html.id)) {
					const dataType: any = setDataType(cell.html, allSize)
					dataType.colSpan = cell.html.colSpan
					dataType.rowSpan = cell.html.rowSpan
					const rawId = cell.html.id
					const cellUid = rawId.startsWith('prefix-') ? rawId.replace('prefix-', '') : rawId
					newRow.push({
						cellUid: cellUid,
						colSpan: cell.html.colSpan,
						rowSpan: cell.html.rowSpan,
						content: cell.html.dataset.type === 'function' ? (cell.html.querySelector('span')?.innerHTML || '') : cell.html.dataset.type === 'image' ? (cell.html.querySelector('img')?.outerHTML || '') : (cell.html.querySelector('div')?.innerHTML || ''),
						dataType: dataType,
					})
					overCell.add(cell.html.id)
				}
			})
			rows.push(newRow)
		})

		// store 및 socket 업데이트
		const blockUid = selector.value.id.includes('temp') ? '' : deletePrefix(selector.value.id)
		const fullBlockId = selector.value.id

		// 현재 table의 ID를 보존
		const currentTableId = table.id ? table.id.replace('prefix-', '') : ''

		let blockContent: any
		blockList.value.forEach((item: any) => {
			const isMatch = item.blockUid === fullBlockId || (blockUid && item.blockUid === blockUid)
			if (isMatch) {
				// content가 문자열인 경우 객체로 변환 (방어 로직)
				if (typeof item.content === 'string' && item.content !== '') {
					try {
						item.content = JSON.parse(item.content)
					} catch (e) {
						item.content = { type: 'defaultTable', cell: [] }
					}
				} else if (!item.content || typeof item.content !== 'object') {
					item.content = { type: 'defaultTable', cell: [] }
				}

				item.content.cell = rows
				if (currentTableId) {
					item.content.tableUID = currentTableId
				}
				blockContent = item.content
			}
		})

		const block: any = document.getElementById(selector.value.id) as HTMLElement
		if (block && blockContent) {
			block.innerHTML = useCreateTable().insertTable(blockContent, blockUid)
			await nextTick()
			const newTable = block.querySelector('table')
			if (newTable) {
				const createTable = useCreateTable()
				createTable.tableShapes(newTable)
				createTable.widthSizeItem(newTable)
				createTable.heightSizeItem(newTable)
			}

			const { dragList } = storeToRefs(useEditorStore())
			dragList.value = []
			cellTarget.value = null

			const existingOverlay = block.querySelector('.table--selector__overlay')
			if (existingOverlay) {
				existingOverlay.remove()
			}

			const activeEl = document.activeElement as HTMLElement
			if (activeEl && block.contains(activeEl)) {
				activeEl.blur()
			}
			window.getSelection()?.removeAllRanges()

			await nextTick()
			calcFunction()
			useCreateTable().event()
		}

		// 테이블 병합 후 직접 socket 저장 (useAddCell 패턴 참고)
		const { noteType, noteUid } = storeToRefs(useEditorStore())
		const { socket } = storeToRefs(useCommonStore())

		if (noteType.value === 'Project' && blockUid && blockContent) {
			const tableUid = deletePrefix(table.id)
			const params = {
				noteUID: noteUid.value,
				blockUID: blockUid,
				new: false,
				content: {
					type: 'defaultTable',
					tableUID: tableUid,
					cell: rows,
				},
			}
			socket.value.createTableBlock(params)
		}

		// 병합/해제 작업 완료 후 플래그 해제 및 후 상태 저장
		if (!wasUndoRedo) {
			isUndoRedoInProgress.value = false
			await nextTick()
			await new UseHistory().push(false, { tableMerge: true })
		}
	}

	return {
		merge,
		unmerge,
		unmergeTarget,
	}
}
